import { useEffect, useRef, useState } from 'react';
import { useApi } from '../../../../hooks/useApi';
import { fmtDate, fmtDateTime } from '../../../../utils/ruDates';
import {
    ESCALATION_LABELS, FAIL_LABELS, REASON_LABELS, REPLY_STATUS_LABELS,
    draftFeedbackReply, getFeedbackItem, replyToFeedback, skipFeedback,
} from '../../../../services/feedback';
import { Stars, errorText, money } from './feedbackUi';

function ReplyEditor({ item, initialText = '', initialNote = '', onChanged }) {
    const { request } = useApi();
    const [text, setText] = useState(initialText);
    const [busy, setBusy] = useState(false);
    const [note, setNote] = useState(initialNote);
    const alive = useRef(true);
    useEffect(() => () => { alive.current = false; }, []);

    // «Проверить без отправки»: ждём исход агента и показываем его, а не оставляем «в очереди».
    const waitForCheck = async () => {
        for (let attempt = 0; attempt < 50 && alive.current; attempt += 1) {
            await new Promise((resolve) => setTimeout(resolve, 6000));
            if (!alive.current) return;
            try {
                const fresh = await getFeedbackItem({ request, itemId: item.id });
                const reply = (fresh.replies || [])[0];
                if (!reply || reply.status === 'queued') continue;
                if (reply.status === 'failed') setNote(`Проверка не прошла: ${FAIL_LABELS[reply.fail_reason] || reply.fail_reason || 'ошибка'}`);
                else setNote('Проверка прошла: агент нашёл карточку, кнопка отправки активна. Ничего не опубликовано.');
                return;
            } catch (error) {
                return;
            }
        }
        if (alive.current) setNote('Агент пока не взял проверку: возможно, занят сбором. Результат появится позже.');
    };

    const draftWithAi = async () => {
        setBusy(true);
        setNote('');
        try {
            const result = await draftFeedbackReply({ request, itemId: item.id });
            if (result.text) {
                setText(result.text);
                setNote(`Черновик от ИИ готов (расход ${money(result.charged_rub)}). Проверьте текст перед отправкой.`);
            } else {
                setNote(`ИИ не стал отвечать: ${ESCALATION_LABELS[result.escalation] || 'нужен человек'}. Расход ${money(result.charged_rub)}.`);
            }
        } catch (error) {
            setNote(errorText(error));
        } finally {
            setBusy(false);
        }
    };

    const submit = async (send, dryRun) => {
        if (send && !dryRun && !window.confirm('Опубликовать ответ в кабинете Ozon? Отозвать его можно только вручную в кабинете.')) return;
        setBusy(true);
        setNote('');
        try {
            await replyToFeedback({ request, itemId: item.id, text, send, dryRun });
            setNote(send ? (dryRun ? 'Проверка поставлена агенту, ждём результат…' : 'Ответ поставлен в очередь агента') : 'Черновик сохранён');
            if (send && !dryRun) onChanged();
            if (send && dryRun) waitForCheck();
        } catch (error) {
            setNote(errorText(error));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="fb-draft">
            <div className="fb-draft__label">{initialText ? 'Черновик ответа от ИИ: отредактируйте и отправьте' : 'Ответ покупателю'}</div>
            <textarea
                className="fb-textarea"
                rows={3}
                maxLength={2000}
                value={text}
                placeholder="Текст ответа (до 2000 символов, без ссылок, телефонов и обещаний денег)"
                onChange={(event) => setText(event.target.value)}
            />
            <div className="fb-actions">
                <button type="button" className="primary" disabled={busy || !text.trim()} onClick={() => submit(true, false)}>Отправить</button>
                <button type="button" disabled={busy} onClick={draftWithAi} title="ИИ подготовит новый черновик; расход списывается с плательщика">
                    {initialText || text ? 'Другой вариант от ИИ' : 'Черновик от ИИ'}
                </button>
                <button type="button" disabled={busy || !text.trim()} onClick={() => submit(true, true)} title="Агент найдёт карточку и проверит форму, ничего не публикуя">Проверить без отправки</button>
                <span className="fb-note">{text.length}/2000</span>
                {note && <span className="fb-note">{note}</span>}
            </div>
        </div>
    );
}

export function ItemCard({ item, onChanged }) {
    const { request } = useApi();
    const [open, setOpen] = useState(false);
    const isReview = item.kind === 'review';
    const isChat = item.kind === 'chat';
    const isSupport = item.kind === 'support';
    const canReply = !isSupport && item.status === 'analyzed' && (!isReview || item.has_text);
    const aiDraft = (item.replies || []).find((reply) => reply.status === 'draft' && reply.source === 'llm');
    const lastSent = (item.replies || []).find((reply) => ['queued', 'moderation', 'sent', 'failed'].includes(reply.status));
    const risky = item.risk_score >= 0.6;

    const skip = async () => {
        try {
            await skipFeedback({ request, itemId: item.id });
            onChanged();
        } catch (error) {
            window.alert(errorText(error));
        }
    };

    return (
        <div className="fb-item">
            <div className="fb-item__head">
                <span className="fb-chip">{isReview ? 'Отзыв' : isChat ? 'Чат' : isSupport ? 'Поддержка' : 'Вопрос'}</span>
                {isReview && <Stars rating={item.rating} />}
                <span className="fb-item__product">{isChat ? (item.buyer_name || 'Покупатель') : isSupport ? (item.product_name || 'Обращение') : (item.product_name || 'Товар')}</span>
                <span className="fb-item__meta">
                    {isSupport ? `Обращение №${item.order_number || '—'}` : isChat ? (item.order_number ? `Заказ ${item.order_number}` : 'Чат с покупателем') + (item.chat_id ? ` · чат ${item.chat_id.slice(0, 8)}` : '') : `SKU ${item.sku || '—'}${item.product_article ? ` · ${item.product_article}` : ''}`}
                    {item.published_at ? ` · ${isChat ? fmtDate(item.published_at) : fmtDateTime(item.published_at)}` : ''}
                </span>
                <span style={{ flex: 1 }} />
                <span className={`fb-chip${risky ? ' fb-chip--bad' : item.status === 'sent' ? ' fb-chip--good' : ''}`}>
                    {REASON_LABELS[item.route_reason] || item.route_reason || '—'}
                </span>
            </div>
            <div className="fb-item__text">
                {item.text || (isReview ? <span className="fb-muted">Покупатель поставил только оценку</span> : '—')}
            </div>
            {item.seller_reply_text && <div className="fb-note">Ответ магазина: {item.seller_reply_text}</div>}
            {lastSent && (
                <div className="fb-note">
                    {REPLY_STATUS_LABELS[lastSent.status] || lastSent.status}
                    {lastSent.fail_reason ? `: ${FAIL_LABELS[lastSent.fail_reason] || lastSent.fail_reason}` : ''}
                </div>
            )}
            {canReply && aiDraft && <ReplyEditor item={item} initialText={aiDraft.text} onChanged={onChanged} />}
            {canReply && !aiDraft && open && <ReplyEditor item={item} onChanged={() => { setOpen(false); onChanged(); }} />}
            {canReply && (
                <div className="fb-actions">
                    {!aiDraft && <button type="button" onClick={() => setOpen((value) => !value)}>{open ? 'Свернуть' : 'Ответить'}</button>}
                    <button type="button" onClick={skip}>Пропустить</button>
                    {item.ozon_status_first_seen && <span className="fb-note">В кабинете был: {item.ozon_status_first_seen}</span>}
                </div>
            )}
        </div>
    );
}
